import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Printer } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { docsAPI, fmtINR } from "@/lib/documents";
import { notifyDocument } from "./documentToast";

const TYPES = [["pool", "Pool & Recreation"], ["restaurant", "Restaurant"], ["room", "Room / Other"]];
const METHODS = ["cash", "card", "upi", "bank_transfer", "ota"];
const cls = "bg-hocp-bgCard border-hocp-border h-8 text-[12px]";

/** "+ Add Service Receipt" — standalone receipt for pool / restaurant / other charges, taxed via hotel tax_config. */
export function ServiceReceiptDialog({ open, onClose, stayId, guestName, onDone }) {
  const [type, setType] = useState("pool");
  const [lines, setLines] = useState([{ description: "", quantity: 1, unit_price: "" }]);
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  const valid = lines.filter((l) => l.description && +l.unit_price > 0);
  useEffect(() => {
    if (!open || valid.length === 0) { setQuote(null); return; }
    const t = setTimeout(() => {
      docsAPI.quote({ receipt_type: type, line_items: valid.map((l) => ({ ...l, quantity: +l.quantity || 1, unit_price: +l.unit_price })) })
        .then(setQuote).catch(() => setQuote(null));
    }, 300);
    return () => clearTimeout(t);
  }, [open, type, JSON.stringify(valid)]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const submit = async () => {
    if (valid.length === 0) { toast.error("Add at least one line with a price"); return; }
    setBusy(true);
    try {
      const rec = await docsAPI.createReceipt({ receipt_type: type, stay_id: stayId, guest_name: guestName, payment_method: method, payment_reference: reference || null,
        line_items: valid.map((l) => ({ description: l.description, quantity: +l.quantity || 1, unit_price: +l.unit_price })) });
      notifyDocument(`Receipt ${rec.receipt_number} issued`, { url: docsAPI.receiptPdfUrl(rec.id), filename: `Receipt-${rec.receipt_number}.pdf`, testId: "service-receipt-toast" });
      setLines([{ description: "", quantity: 1, unit_price: "" }]); setReference("");
      onDone?.(); onClose();
    } catch (e) { toast.error(formatApiError(e) || "Could not issue receipt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-hocp-bgSubtle border-hocp-border max-w-xl" data-testid="service-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Service Receipt</DialogTitle>
          <DialogDescription className="text-[12px]">Standalone receipt for pool, restaurant or other services paid on the spot. GST is applied from the hotel's tax configuration.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[10px] uppercase tracking-wider text-hocp-textMuted">Receipt type</Label>
            <Select value={type} onValueChange={setType}><SelectTrigger className={`${cls} mt-1`} data-testid="service-receipt-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-[10px] uppercase tracking-wider text-hocp-textMuted">Payment method</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger className={`${cls} mt-1`} data-testid="service-receipt-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[10px] uppercase tracking-wider text-hocp-textMuted">Items</Label>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setLines([...lines, { description: "", quantity: 1, unit_price: "" }])} data-testid="service-receipt-add-line"><Plus className="w-3 h-3 mr-1" />Add line</Button>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-1.5" data-testid={`service-line-${i}`}>
              <Input className={`${cls} col-span-6`} placeholder="Pool Day Pass — Adult" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`service-line-desc-${i}`} />
              <Input className={`${cls} col-span-2 mono`} type="number" min="1" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`service-line-qty-${i}`} />
              <Input className={`${cls} col-span-3 mono`} type="number" step="0.01" placeholder="500" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`service-line-price-${i}`} />
              <button type="button" className="col-span-1 text-rose-400 hover:text-rose-300" onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 mx-auto" /></button>
            </div>))}
        </div>
        <div><Label className="text-[10px] uppercase tracking-wider text-hocp-textMuted">Reference no.</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} className={`${cls} mt-1 mono`} placeholder="UPI8834521" data-testid="service-receipt-reference" /></div>
        <div className="border border-hocp-border rounded-md p-3 text-[12px] space-y-1" data-testid="service-receipt-quote">
          <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="mono">{fmtINR(quote?.subtotal || 0)}</span></div>
          {(quote?.taxes || []).map((t, i) => <div key={i} className="flex justify-between text-muted-foreground"><span>{t.name} @ {t.rate}%</span><span className="mono">{fmtINR(t.amount)}</span></div>)}
          <div className="flex justify-between font-semibold border-t border-hocp-border pt-1"><span>Total to collect</span><span className="mono" data-testid="service-receipt-total">{fmtINR(quote?.total || 0)}</span></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="h-9 text-[12px]">Cancel</Button>
          <Button disabled={busy || valid.length === 0} onClick={submit} className="h-9 text-[12px] bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="service-receipt-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />}Issue & Print Receipt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
